import { environment } from '../environments/environment';
import { inAppBrowser } from './sentry';

type TrackerModule = typeof import('@openreplay/tracker');
type Tracker = InstanceType<TrackerModule['default']>;

/** Every card field the landing checkout renders. */
const CARD_FIELD = [
  'input[autocomplete^="cc-"]',
  'input[name="cardnumber"]',
  'input[name="ccname"]',
  'input[name="exp"]',
  'input[name="cvc"]',
].join(', ');

/** A form holding one of these is a card form: all of its inputs are hidden. */
const CARD_FORM_MARKER = 'input[autocomplete="cc-number"], input[name="cardnumber"]';

// SanitizeLevel values, inlined so the tracker stays out of the main bundle.
const PLAIN = 0;
const HIDDEN = 2;

/** Card inputs are never recorded, not even as masked length. */
export function openReplayDomSanitizer(node: Element): number {
  if (node.tagName !== 'INPUT' && node.tagName !== 'SELECT') {
    return PLAIN;
  }
  if (node.matches(CARD_FIELD)) {
    return HIDDEN;
  }
  return node.closest('form')?.querySelector(CARD_FORM_MARKER) ? HIDDEN : PLAIN;
}

/** Query values that can grant access (CV codes, tokens, OTPs) are masked in page URLs. */
const SECRET_PARAM =
  /([?&][^=&#]*(?:code|token|otp|key|secret|signature|password|hash)[^=&#]*=)[^&#]*/gi;

export function maskSecretParams(url: string): string {
  return url.replace(SECRET_PARAM, '$1***');
}

/** Request and resource URLs are recorded without their query string. */
export function stripQuery(url: string): string {
  return url.split('?')[0];
}

// Message ids of the pinned tracker (17.1.6): [id, url, referrer, ...].
const SET_PAGE_LOCATION = 122;
const SET_PAGE_LOCATION_OLD = 4;

/**
 * `urlSanitizer` covers only the page URL. The tracker (17.1.6, still so in
 * 19.x) also sends the PREVIOUS page URL raw as the referrer of each in-site
 * navigation, and the URL at start raw in every batch header: mask both.
 * Relies on the pinned version's internals; does nothing if they differ.
 */
export function maskTrackerUrls(t: unknown): void {
  const app = (t as { app?: any })?.app;
  if (!app || typeof app.send !== 'function') {
    return;
  }
  const send = app.send;
  app.send = (message: unknown, urgent?: boolean) => {
    if (
      Array.isArray(message) &&
      (message[0] === SET_PAGE_LOCATION || message[0] === SET_PAGE_LOCATION_OLD) &&
      typeof message[2] === 'string'
    ) {
      message[2] = maskSecretParams(message[2]);
    }
    return send(message, urgent);
  };
  const worker = app.worker;
  if (worker && typeof worker.postMessage === 'function') {
    const post = worker.postMessage.bind(worker);
    worker.postMessage = (data: any, ...rest: any[]) =>
      post(
        data?.type === 'start' && typeof data.url === 'string'
          ? { ...data, url: maskSecretParams(data.url) }
          : data,
        ...rest,
      );
  }
}

let tracker: Tracker | null = null;
let active = false;
/** Metadata set before the tracker starts; applied once it does. */
const pendingMetadata = new Map<string, string>();

/**
 * Session recording for uptal.com (OpenReplay, self-hosted at replay.uptal.com),
 * LAZY like Sentry: the tracker is a separate chunk fetched once the page has
 * loaded and the browser is idle, so the first paint every ad click waits on
 * carries none of it. Never throws: if the ingest is unreachable the page
 * carries on unrecorded. Sessions are tagged `surface: landing` and `in_app`,
 * and with the visitor's `cv_code` once they upload (the app tags the same
 * key), so one search shows a visitor's whole journey.
 */
export function initOpenReplay(): void {
  const config = environment.openReplay;
  if (!config?.enabled || typeof window === 'undefined') {
    return;
  }
  active = true;
  pendingMetadata.set('surface', 'landing');
  pendingMetadata.set('in_app', inAppBrowser(navigator.userAgent) || 'none');
  const load = (): void => {
    import('@openreplay/tracker')
      .then(async ({ default: TrackerClass }) => {
        const t = new TrackerClass({
          projectKey: config.projectKey,
          ingestPoint: config.ingestPoint,
          // Typed values are recorded as masked length only; emails in page text are masked.
          defaultInputMode: 1,
          obscureInputNumbers: true,
          obscureInputEmails: true,
          obscureTextEmails: true,
          domSanitizer: openReplayDomSanitizer as any,
          // Network: URL (without query string), method, status and timing only.
          network: {
            capturePayload: false,
            ignoreHeaders: true,
            sessionTokenHeader: false,
            failuresOnly: false,
            captureInIframes: false,
            sanitizer: (data) => {
              data.url = stripQuery(data.url);
              return data;
            },
          },
          resourceNameSanitizer: stripQuery,
          urls: { urlSanitizer: maskSecretParams },
          canvas: { disableCanvas: true },
        });
        maskTrackerUrls(t);
        const result = await t.start();
        if (!result.success) {
          active = false;
          pendingMetadata.clear();
          return;
        }
        tracker = t;
        pendingMetadata.forEach((value, key) => t.setMetadata(key, value));
        pendingMetadata.clear();
      })
      .catch(() => {
        active = false;
        pendingMetadata.clear();
      });
  };
  const whenIdle = (): void => {
    const idle = (window as Window & { requestIdleCallback?: Function })
      .requestIdleCallback;
    if (typeof idle === 'function') {
      idle(load, { timeout: 4000 });
    } else {
      setTimeout(load, 1500);
    }
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
}

/** Tag the session (e.g. `cv_code`); queued until the tracker starts. Never throws. */
export function openReplayMetadata(key: string, value: string | null | undefined): void {
  if (!value || !active) {
    return;
  }
  try {
    if (tracker) {
      tracker.setMetadata(key, value);
    } else {
      pendingMetadata.set(key, value);
    }
  } catch {
    /* recording must never break the page */
  }
}
